import React, { useState } from 'react';
import { ChevronDown, Clock, FileText, PlayCircle } from 'lucide-react';
import { buildLessonspaceRecordingUrl } from '../lib/lessonspace';

const VALID_RATINGS = ['strong', 'adequate', 'needs_development'];

export const ratingLabel = (r) =>
  r === 'strong' ? 'Strong' : r === 'adequate' ? 'Adequate' : r === 'needs_development' ? 'Needs Dev' : '—';

const ratingClass = (r, isDark) =>
  r === 'strong' ? (isDark ? 'text-[#7662ac]' : 'text-[#586f8d]')
    : r === 'adequate' ? (isDark ? 'text-[#a1a1aa]' : 'text-[#666]')
      : r === 'needs_development' ? 'text-amber-400'
        : (isDark ? 'text-[#52525b]' : 'text-[#999]');

const cleanText = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);

const normalizeRating = (r) => (typeof r === 'string' && VALID_RATINGS.includes(r) ? r : null);

const toSeconds = (v) => {
  if (typeof v === 'number' && Number.isFinite(v)) return Math.max(0, Math.floor(v));
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Math.max(0, Math.floor(Number(v)));
  return null;
};

// "24:23" -> 1463, "1:04:23" -> 3863. Returns null for anything that isn't a
// clean colon-separated clock reading, so junk never becomes a bogus deep link.
const displayToSeconds = (display) => {
  if (typeof display !== 'string') return null;
  const parts = display.trim().split(':');
  if (parts.length < 2 || parts.length > 3) return null;
  let total = 0;
  for (const part of parts) {
    if (!/^\d{1,3}$/.test(part)) return null;
    total = total * 60 + Number(part);
  }
  return Number.isFinite(total) ? total : null;
};

// Inverse of the above, used only when a citation carries seconds but the
// backend never wrote a display string.
const secondsToDisplay = (seconds) => {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds < 0) return null;
  const total = Math.floor(seconds);
  const s = total % 60;
  const m = Math.floor(total / 60) % 60;
  const h = Math.floor(total / 3600);
  const pad = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
};

const normalizeCitations = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((c) => {
      if (!c || typeof c !== 'object') return null;
      const quote = cleanText(c.quote);
      // `moment` is the newer field: a clause describing what actually happened.
      // `quote` is now optional, so a citation is kept when it has either.
      const moment = cleanText(c.moment);
      if (!quote && !moment) return null;
      const timestampDisplay = cleanText(c.timestamp_display);
      return {
        quote,
        moment,
        timestampDisplay,
        timestampSeconds: toSeconds(c.timestamp_seconds) ?? displayToSeconds(timestampDisplay),
      };
    })
    .filter(Boolean);
};

// The legacy five-dimension shape, used whenever `quality_assessments.dimensions`
// is missing (every assessment written before the JSONB column existed).
const LEGACY_DIMENSIONS = [
  { key: 'clarity', label: 'Clarity' },
  { key: 'engagement', label: 'Engagement' },
  { key: 'adaptability', label: 'Adaptability' },
  { key: 'encouragement', label: 'Encouragement' },
  { key: 'structure', label: 'Structure' },
];

// Normalises a quality_assessments row into one ordered dimension array so the
// UI has a single render path for both the new and the legacy column layout.
export const buildQualityDimensions = (qa) => {
  if (!qa || qa.insufficient_evidence) return [];

  const raw = qa.dimensions;
  if (Array.isArray(raw) && raw.length > 0) {
    const mapped = raw
      .map((d, i) => {
        if (!d || typeof d !== 'object') return null;
        const key = cleanText(d.key);
        const label = cleanText(d.label) || key || `Dimension ${i + 1}`;
        return {
          id: `${i}-${key || 'dimension'}`,
          label,
          rating: normalizeRating(d.rating),
          evidence: cleanText(d.evidence),
          citations: normalizeCitations(d.citations),
        };
      })
      .filter(Boolean);
    if (mapped.length > 0) return mapped;
  }

  return LEGACY_DIMENSIONS.map((d, i) => ({
    id: `${i}-${d.key}`,
    label: d.label,
    rating: normalizeRating(qa[`${d.key}_rating`]),
    evidence: cleanText(qa[`${d.key}_evidence`]),
    citations: [],
  }));
};

// Dimension count is org-configurable (1-5), so the chip grid tracks it.
const gridColsClass = (n) => {
  if (n <= 1) return 'grid-cols-1';
  if (n === 2) return 'grid-cols-2';
  if (n === 3) return 'grid-cols-2 sm:grid-cols-3';
  if (n === 4) return 'grid-cols-2 sm:grid-cols-4';
  return 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5';
};

/* ------------------------------------------------------------------ *
 * Narrative evidence: "... at [24:23] they asked the student to ..."  *
 * ------------------------------------------------------------------ */

// Only a fully-formed, closed clock marker counts. An unclosed "[", "[n/a]" or
// ordinary prose brackets simply stay inside the surrounding text run.
const TIME_MARKER_SOURCE = '\\[\\s*(\\d{1,3}:\\d{1,2}(?::\\d{1,2})?)\\s*\\]';

// Splits narrative evidence into plain text runs and time markers. Never uses
// dangerouslySetInnerHTML — the caller renders the returned segments as nodes.
const parseEvidenceSegments = (text) => {
  if (typeof text !== 'string' || !text) return [];
  const segments = [];
  const re = new RegExp(TIME_MARKER_SOURCE, 'g');
  let last = 0;
  let match;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) segments.push({ type: 'text', value: text.slice(last, match.index) });
    const display = match[1].trim();
    segments.push({ type: 'time', display, seconds: displayToSeconds(display) });
    last = match.index + match[0].length;
    if (re.lastIndex === match.index) re.lastIndex += 1; // defensive: never spin
  }
  if (last < text.length) segments.push({ type: 'text', value: text.slice(last) });
  return segments;
};

// Marker -> citation lookup. Exact display string first (cheapest and most
// literal), then exact seconds, then the nearest citation within two seconds so
// a marker of [24:23] still finds a citation stamped at 1462.
const findCitationForMarker = (citations, marker) => {
  if (!citations.length) return null;
  const exact = citations.find((c) => c.timestampDisplay && c.timestampDisplay === marker.display);
  if (exact) return exact;
  if (marker.seconds == null) return null;
  const sameSeconds = citations.find((c) => c.timestampSeconds === marker.seconds);
  if (sameSeconds) return sameSeconds;
  let best = null;
  let bestDelta = Infinity;
  citations.forEach((c) => {
    if (c.timestampSeconds == null) return;
    const delta = Math.abs(c.timestampSeconds - marker.seconds);
    if (delta <= 2 && delta < bestDelta) { best = c; bestDelta = delta; }
  });
  return best;
};

const citationDetail = (citation) => {
  if (!citation) return null;
  return [citation.moment, citation.quote ? `“${citation.quote}”` : null].filter(Boolean).join(' — ') || null;
};

// Decides, per dimension, whether we're looking at the new narrative shape or
// the old prose + bare-quote shape, and pre-resolves everything the renderer
// needs so the JSX below stays declarative.
const prepareDimension = (d) => {
  const citations = Array.isArray(d && d.citations)
    ? d.citations.filter((c) => c && typeof c === 'object' && (cleanText(c.quote) || cleanText(c.moment)))
    : [];
  const evidence = cleanText(d && d.evidence);
  const segments = parseEvidenceSegments(evidence || '');

  const matched = new Set();
  const resolved = segments.map((seg) => {
    if (seg.type !== 'time') return seg;
    const citation = findCitationForMarker(citations, seg);
    if (citation) matched.add(citation);
    return { ...seg, citation };
  });

  const markerKeys = new Set();
  resolved.forEach((seg) => {
    if (seg.type === 'time') markerKeys.add(seg.seconds != null ? `s${seg.seconds}` : `d${seg.display}`);
  });

  // New shape is signalled by inline markers in the prose and/or citations that
  // carry a `moment` clause. Anything else is treated as the old shape.
  const hasMarkers = markerKeys.size > 0;
  const hasMoments = citations.some((c) => cleanText(c.moment));
  const narrative = hasMarkers || hasMoments;

  // Moments the prose never referenced would otherwise be invisible, so they get
  // a compact supporting line. Citations with only a quote are deliberately
  // dropped here: a bare quote with no context is exactly what we're replacing.
  const supporting = narrative
    ? citations.filter((c) => cleanText(c.moment) && !matched.has(c))
    : [];
  const legacyCitations = narrative ? [] : citations.filter((c) => cleanText(c.quote));

  return {
    evidence,
    segments: resolved,
    narrative,
    supporting,
    legacyCitations,
    hintCount: narrative ? markerKeys.size + supporting.length : legacyCitations.length,
    hintNoun: narrative ? 'moment' : 'quote',
    hasContent: !!evidence || supporting.length > 0 || legacyCitations.length > 0,
  };
};

// An inline, in-sentence time reference. Renders as a quiet chip that jumps the
// transcript, plus an optional adjacent icon that opens the recording at that
// moment. Wrapped in a nowrap span so the pair never splits across lines.
const InlineTimestamp = ({ display, seconds, citation, isDark, recording, onJumpToTranscript }) => {
  const effectiveSeconds = seconds != null ? seconds : (citation ? citation.timestampSeconds : null);
  const watchUrl = effectiveSeconds != null
    ? buildLessonspaceRecordingUrl({ ...(recording || {}), timestampSeconds: effectiveSeconds })
    : null;
  const canJump = typeof onJumpToTranscript === 'function' && !!display;
  const detail = citationDetail(citation);
  const title = detail ? `${display} — ${detail}` : `Jump to ${display} in the transcript`;

  if (!display) return null;

  const chipBase = 'inline-flex items-baseline align-baseline rounded px-1 text-[0.8em] font-medium tabular-nums leading-normal';
  const chipTone = isDark
    ? 'bg-[#7662ac]/15 text-[#7662ac]'
    : 'bg-[#586f8d]/10 text-[#586f8d]';
  const chipHover = isDark ? 'hover:bg-[#7662ac]/30 hover:text-white' : 'hover:bg-[#586f8d]/20 hover:text-[#1a1a1a]';

  return (
    <span className="whitespace-nowrap">
      {canJump ? (
        <button
          type="button"
          title={title}
          onClick={() => onJumpToTranscript(display)}
          className={`${chipBase} ${chipTone} ${chipHover} transition-colors`}
        >
          {display}
        </button>
      ) : (
        <span title={detail || undefined} className={`${chipBase} ${chipTone}`}>{display}</span>
      )}
      {watchUrl && (
        <a
          href={watchUrl}
          target="_blank"
          rel="noopener noreferrer"
          title={`Watch the recording from ${display}`}
          className={`ml-0.5 inline-block align-middle transition-colors ${
            isDark ? 'text-[#52525b] hover:text-[#7662ac]' : 'text-[#999] hover:text-[#586f8d]'
          }`}
        >
          <PlayCircle className="h-3 w-3" />
        </a>
      )}
    </span>
  );
};

// The narrative paragraph, with every [MM:SS] marker swapped for an inline chip.
const NarrativeEvidence = ({ segments, isDark, recording, onJumpToTranscript }) => (
  <p className={`text-sm leading-relaxed break-words ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
    {segments.map((seg, i) =>
      seg.type === 'time' ? (
        <InlineTimestamp
          key={i}
          display={seg.display}
          seconds={seg.seconds}
          citation={seg.citation}
          isDark={isDark}
          recording={recording}
          onJumpToTranscript={onJumpToTranscript}
        />
      ) : (
        <React.Fragment key={i}>{seg.value}</React.Fragment>
      )
    )}
  </p>
);

// A moment the prose didn't cite inline. Description first, quote only ever as
// trailing secondary detail — never a card whose content is a bare quote.
const SupportingMoment = ({ citation, isDark, recording, onJumpToTranscript }) => {
  // Timestamps may be null when the backend couldn't verify them; the moment is
  // still worth showing, just without a jump target.
  const display = citation.timestampDisplay || secondsToDisplay(citation.timestampSeconds);
  return (
    <div className={`flex items-start gap-2 text-xs leading-relaxed ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
      {display ? (
        <InlineTimestamp
          display={display}
          seconds={citation.timestampSeconds}
          citation={citation}
          isDark={isDark}
          recording={recording}
          onJumpToTranscript={onJumpToTranscript}
        />
      ) : (
        <Clock className={`mt-0.5 h-3 w-3 flex-shrink-0 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`} />
      )}
      <span className="min-w-0 flex-1 break-words">
        {citation.moment}
        {citation.quote && (
          <span className={`italic ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}> — &ldquo;{citation.quote}&rdquo;</span>
        )}
      </span>
    </div>
  );
};

// Legacy rendering, kept byte-for-byte in look so old rows don't regress.
const Citation = ({ citation, isDark, recording, onJumpToTranscript }) => {
  const watchUrl = citation.timestampSeconds != null
    ? buildLessonspaceRecordingUrl({ ...(recording || {}), timestampSeconds: citation.timestampSeconds })
    : null;
  const canJump = !!citation.timestampDisplay && typeof onJumpToTranscript === 'function';
  const hasMeta = !!citation.timestampDisplay || !!watchUrl;

  const actionClass = `inline-flex items-center gap-1 text-xs transition-colors ${
    isDark ? 'text-[#7662ac] hover:text-white' : 'text-[#586f8d] hover:text-[#1a1a1a]'
  }`;

  return (
    <div className={`p-3 rounded-lg border-l-2 ${isDark ? 'bg-[#18181b] border-[#586f8d]' : 'bg-[#f5f5f5] border-[#586f8d]'}`}>
      <p className={`text-sm italic break-words ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
        &ldquo;{citation.quote}&rdquo;
      </p>
      {hasMeta && (
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5">
          {citation.timestampDisplay && (
            <span className={`inline-flex items-center gap-1 text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
              <Clock className="h-3 w-3" /> {citation.timestampDisplay}
            </span>
          )}
          {watchUrl && (
            <a href={watchUrl} target="_blank" rel="noopener noreferrer" className={actionClass}>
              <PlayCircle className="h-3 w-3" /> Watch
            </a>
          )}
          {canJump && (
            <button type="button" onClick={() => onJumpToTranscript(citation.timestampDisplay)} className={actionClass}>
              <FileText className="h-3 w-3" /> Go to transcript
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const QualityEvidence = ({ dimensions, isDark, recording, onJumpToTranscript }) => {
  const dims = (Array.isArray(dimensions) ? dimensions : [])
    .filter((d) => d && typeof d === 'object')
    .map((d, i) => ({ ...d, id: d.id != null ? d.id : `dimension-${i}`, prepared: prepareDimension(d) }));
  const withEvidence = dims.filter((d) => d.prepared.hasContent);
  const [openId, setOpenId] = useState(() => (withEvidence.length > 0 ? withEvidence[0].id : null));

  if (dims.length === 0) return null;

  return (
    <>
      {/* At-a-glance ratings */}
      <div className={`grid ${gridColsClass(dims.length)} gap-3`}>
        {dims.map((d) => (
          <div key={d.id} className={`p-3 rounded-lg text-center min-w-0 ${isDark ? 'bg-[#18181b]' : 'bg-[#f5f5f5]'}`}>
            <div className={`text-xs mb-1 leading-tight break-words ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>{d.label}</div>
            <div className={`text-sm font-medium ${ratingClass(d.rating, isDark)}`}>{ratingLabel(d.rating)}</div>
          </div>
        ))}
      </div>

      {/* Why each rating was given */}
      {withEvidence.length > 0 && (
        <div className={`mt-4 pt-4 border-t ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
          <div className={`text-xs font-medium uppercase tracking-wider mb-2 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
            Evidence
          </div>
          <div className={`rounded-lg border overflow-hidden ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
            {withEvidence.map((d, i) => {
              const isOpen = openId === d.id;
              const p = d.prepared;
              return (
                <div
                  key={d.id}
                  className={i > 0 ? `border-t ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}` : ''}
                >
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    onClick={() => setOpenId(isOpen ? null : d.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors ${
                      isDark ? 'hover:bg-[#18181b]' : 'hover:bg-[#f9f9f9]'
                    }`}
                  >
                    <ChevronDown
                      className={`h-3.5 w-3.5 flex-shrink-0 transition-transform ${isOpen ? '' : '-rotate-90'} ${
                        isDark ? 'text-[#52525b]' : 'text-[#999]'
                      }`}
                    />
                    <span className={`flex-1 min-w-0 text-sm font-medium break-words ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
                      {d.label}
                    </span>
                    {p.hintCount > 0 && (
                      <span className={`hidden sm:inline text-xs flex-shrink-0 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                        {p.hintCount} {p.hintNoun}{p.hintCount !== 1 ? 's' : ''}
                      </span>
                    )}
                    <span className={`text-xs font-medium flex-shrink-0 ${ratingClass(d.rating, isDark)}`}>
                      {ratingLabel(d.rating)}
                    </span>
                  </button>

                  {isOpen && (
                    <div className="px-3 pb-3 space-y-3">
                      {p.evidence && (
                        <NarrativeEvidence
                          segments={p.segments}
                          isDark={isDark}
                          recording={recording}
                          onJumpToTranscript={onJumpToTranscript}
                        />
                      )}
                      {p.supporting.length > 0 && (
                        <div className={`space-y-1.5 border-l pl-3 ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
                          {p.supporting.map((c, ci) => (
                            <SupportingMoment
                              key={ci}
                              citation={c}
                              isDark={isDark}
                              recording={recording}
                              onJumpToTranscript={onJumpToTranscript}
                            />
                          ))}
                        </div>
                      )}
                      {p.legacyCitations.map((c, ci) => (
                        <Citation
                          key={ci}
                          citation={c}
                          isDark={isDark}
                          recording={recording}
                          onJumpToTranscript={onJumpToTranscript}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};

export default QualityEvidence;
